/** @format */

import { FC, useMemo, useRef } from "react";
import { UploadCloud, X } from "lucide-react";
import { cn } from "../../utils/cn";
import {
  type ReferenceFileKind,
  getReferenceFileKey,
} from "../../store/useReviewCommitStore";

interface ReferenceFileUploaderProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  defaultKind?: ReferenceFileKind;
  onDefaultKindChange?: (kind: ReferenceFileKind) => void;
  /** Per-file kind (key = getReferenceFileKey(file)); used to show type badge. */
  referenceFileKinds?: Record<string, ReferenceFileKind>;
}

const KIND_ACCEPT: Record<ReferenceFileKind, string[]> = {
  bom: [
    // tabular formats
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".csv",
    ".xls",
    ".xlsx",
    // BOM-as-PDF (common in the wild)
    "application/pdf",
    ".pdf",
  ],
  assembly: [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/svg+xml",
    "image/bmp",
    "image/tiff",
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".webp",
  ],
  video: [
    "video/mp4",
    "video/quicktime",
    "video/avi",
    "video/x-matroska",
    "video/mkv",
    ".mp4",
    ".mov",
    ".avi",
    ".mkv",
  ],
  step: ["application/octet-stream", ".stp", ".step"],
};

function fileMatchesKind(file: File, kind: ReferenceFileKind): boolean {
  const patterns = KIND_ACCEPT[kind] ?? [];
  const lowerName = file.name.toLowerCase();
  return patterns.some((pattern) => {
    if (pattern.startsWith(".")) {
      return lowerName.endsWith(pattern.toLowerCase());
    }
    return file.type === pattern;
  });
}

const MAX_REFERENCE_FILES: number = 5;

function fileKey(file: File): string {
  return `${file.name}:${file.size}`;
}

const KIND_OPTIONS: { value: ReferenceFileKind; label: string }[] = [
  { value: "bom", label: "BOM file" },
  { value: "assembly", label: "Assembly file" },
  { value: "video", label: "Video" },
  { value: "step", label: "STEP (.stp/.step)" },
];

const KIND_BADGE_LABELS: Record<ReferenceFileKind, string> = {
  bom: "BOM",
  assembly: "Assembly",
  video: "Video",
  step: "STEP",
};

const ReferenceFileUploader: FC<ReferenceFileUploaderProps> = ({
  files,
  onFilesChange,
  defaultKind,
  onDefaultKindChange,
  referenceFileKinds = {},
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const existingKeys = useMemo(() => new Set(files.map(fileKey)), [files]);

  const handleFiles = (fileList: FileList) => {
    const added = Array.from(fileList);
    const accepted: File[] = [];
    const rejected: string[] = [];
    const duplicates: string[] = [];

    added.forEach((file) => {
      if (!fileMatchesKind(file, defaultKindValue)) {
        rejected.push(file.name);
        return;
      }
      if (existingKeys.has(fileKey(file))) {
        duplicates.push(file.name);
        return;
      }
      accepted.push(file);
    });

    if (rejected.length) {
      alert(
        `These files do not match the selected type (${
          KIND_OPTIONS.find((o) => o.value === defaultKindValue)?.label ??
          defaultKindValue
        }):\n\n${rejected.join("\n")}`,
      );
    }

    if (duplicates.length) {
      alert(
        `Duplicate file(s) not added (already in list):\n\n${duplicates.join("\n")}`,
      );
    }

    if (accepted.length) {
      const currentCount = files.length;
      const totalAfter = currentCount + accepted.length;
      const toAdd =
        totalAfter <= MAX_REFERENCE_FILES
          ? accepted
          : accepted.slice(0, MAX_REFERENCE_FILES - currentCount);
      const next = [...files, ...toAdd];
      onFilesChange(next);
      if (totalAfter > MAX_REFERENCE_FILES) {
        const fileWord = MAX_REFERENCE_FILES === 1 ? "file" : "files";
        if (toAdd.length === 0) {
          alert(
            `Maximum ${MAX_REFERENCE_FILES} reference ${fileWord} allowed. Remove a file first to add another.`,
          );
        } else {
          alert(
            `Maximum ${MAX_REFERENCE_FILES} reference ${fileWord}. Only the first ${toAdd.length} of the new selection were added.`,
          );
        }
      }
    }
  };

  const removeAt = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    onFilesChange([]);
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    if (event.dataTransfer.files?.length) {
      handleFiles(event.dataTransfer.files);
    }
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
  };

  const defaultKindValue: ReferenceFileKind = useMemo(() => {
    return defaultKind ?? "step";
  }, [defaultKind]);

  const acceptAttr = useMemo(
    () => (KIND_ACCEPT[defaultKindValue] ?? []).join(","),
    [defaultKindValue],
  );

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-200">
        Upload new files (max {MAX_REFERENCE_FILES})
      </p>

      {onDefaultKindChange && (
        <div className="flex items-center justify-between gap-3">
          <label className="text-xs text-slate-400">
            File type (applies to new files)
          </label>
          <select
            value={defaultKindValue}
            onChange={(e) =>
              onDefaultKindChange(e.target.value as ReferenceFileKind)
            }
            className="text-xs rounded-md border border-slate-700/70 bg-slate-950/60 text-slate-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            {KIND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        className={cn(
          "rounded-xl border border-dashed border-slate-700/70",
          "bg-slate-950/70 hover:border-blue-500/70 transition-colors",
          "px-4 py-6 flex flex-col items-center justify-center gap-2 text-center",
        )}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => inputRef.current?.click()}
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/30 to-cyan-500/30 flex items-center justify-center mb-1">
          <UploadCloud className="w-5 h-5 text-blue-200" />
        </div>
        <p className="text-sm text-slate-100">
          Click to upload or drag & drop reference files
        </p>
        <p className="text-xs text-slate-500">
          Upload BOMs, Assembly docs, Videos, STEP files.
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          multiple
          className="hidden"
          onChange={(event) => {
            if (event.target.files) {
              handleFiles(event.target.files);
              event.target.value = "";
            }
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="modal-body-scroll rounded-lg border border-slate-800/70 bg-slate-900/40 px-3 py-2 text-xs text-slate-300 space-y-2 max-h-40 overflow-y-auto mr-2">
          <div className="flex items-center justify-between">
            <span className="text-slate-300">
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-red-300 hover:text-red-200 underline-offset-2 hover:underline"
            >
              Clear
            </button>
          </div>
          {files.map((file, idx) => {
            const fileKind =
              referenceFileKinds[getReferenceFileKey(file)] ?? defaultKindValue;
            const badgeLabel =
              KIND_BADGE_LABELS[fileKind] ?? KIND_BADGE_LABELS.step;
            return (
              <div
                key={`${file.name}-${idx}`}
                className="flex items-center justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="truncate block">{file.name}</span>
                    <span
                      className={cn(
                        "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0",
                        fileKind === "bom" &&
                          "bg-amber-500/20 text-amber-200 border border-amber-500/40",
                        fileKind === "assembly" &&
                          "bg-blue-500/20 text-blue-200 border border-blue-500/40",
                        fileKind === "video" &&
                          "bg-purple-500/20 text-purple-200 border border-purple-500/40",
                        fileKind === "step" &&
                          "bg-slate-500/20 text-slate-200 border border-slate-500/40",
                      )}
                    >
                      {badgeLabel}
                    </span>
                  </div>
                  <span className="text-slate-500">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className={cn(
                    "inline-flex items-center justify-center rounded-full border text-[11px] font-semibold transition-colors",
                    "w-6 h-6 border-slate-700/70 bg-slate-950/40 text-slate-300 hover:bg-slate-900/60",
                  )}
                  aria-label="Remove file"
                  title="Remove file"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReferenceFileUploader;
